import React, { useState, useRef, useEffect } from 'react';
import { HelpIcon } from '../../Icons/HelpIcon/HelpIcon';
import './Dropdown.css';

export interface DropdownOption {
	value: string;
	label: string;
	description?: string;
}

export interface DropdownProps {
	options: DropdownOption[];
	value: string;
	onChange: (value: string) => void;
	label?: string;
	id?: string;
	className?: string;
	disabled?: boolean;
	showLabel?: boolean;
	showValidation?: boolean;
	requiredField?: boolean;
	showHelpIcon?: boolean;
	helpText?: string;
	state?: 'default' | 'error' | 'success';
	placeholder?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
	options,
	value,
	onChange,
	label = '',
	id,
	className = '',
	disabled = false,
	showLabel = true,
	showValidation = false,
	requiredField = false,
	showHelpIcon = false,
	helpText = '',
	state = 'default',
	placeholder = '',
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const dropdownId = id;

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleDropdownClick = () => {
		if (!disabled) {
			setIsOpen(!isOpen);
		}
	};

	const handleOptionClick = (optionValue: string) => {
		if (!disabled) {
			onChange(optionValue);
			setIsOpen(false);
		}
	};

	const getDisplayValue = () => {
		const selectedOption = options.find((option) => option.value === value);
		return selectedOption ? selectedOption.label : placeholder || 'Select an option';
	};

	return (
		<div
			className={`dropdown ${className} ${disabled ? 'dropdown-disabled' : ''}`.trim()}
			data-name="Dropdown"
			ref={dropdownRef}
			aria-disabled={disabled}
		>
			<div
				className={`dropdown-state-default ${state !== 'default' ? `dropdown-state-${state}` : ''}`}
				data-name="State=Default"
			>
				{showLabel && (
					<div className="dropdown-label-body" data-name="Label body">
						<label id={dropdownId + '-label'} className="dropdown-label-text">
							{label}
						</label>
						{requiredField && <span className="dropdown-required-indicator">*</span>}
						{showHelpIcon && helpText && (
							<div className="dropdown-help-icon-container">
								<HelpIcon helpText={helpText} className="dropdown-help-icon" />
							</div>
						)}
					</div>
				)}
				<div className="dropdown-body" data-name="Dropdown body">
					<div
						className={`dropdown-content ${isOpen ? 'dropdown-content-open' : ''}`}
						data-name="Dropdown content"
						onClick={handleDropdownClick}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handleDropdownClick();
							}
						}}
						tabIndex={disabled ? -1 : 0}
						role="button"
						aria-expanded={isOpen}
						aria-haspopup="listbox"
						aria-labelledby={showLabel ? dropdownId + '-label' : undefined}
					>
						<div className="dropdown-left-content">
							<div className="dropdown-text-container">
								<span className="dropdown-selected-text">{getDisplayValue()}</span>
							</div>
						</div>
						<div className="dropdown-arrow-container" data-name="State=Active">
							<svg
								className={`dropdown-arrow ${isOpen ? 'dropdown-arrow-open' : ''}`}
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M7 10l5 5 5-5"
									stroke="#000000"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</div>
					</div>
					{isOpen && (
						<div className="dropdown-options" role="listbox">
							{options.map((option) => (
								<div
									key={option.value}
									className={`dropdown-option ${value === option.value ? 'dropdown-option-selected' : ''}`}
									onClick={() => handleOptionClick(option.value)}
									role="option"
									aria-selected={value === option.value}
								>
									{option.label}
								</div>
							))}
						</div>
					)}
					{showValidation && state === 'error' && (
						<div className="dropdown-validation-message">
							Please select a valid option
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
